/**
 * TokenContext - Gestion du solde de crédits/tokens
 * CABINET 4.0 - Système de crédits pour recherches IA
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { tokensAPI } from '../lib/api';

const TokenContext = createContext(null);

export const TokenProvider = ({ children }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [tier, setTier] = useState('free');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastTransaction, setLastTransaction] = useState(null);

  // Charger le solde
  const fetchBalance = useCallback(async () => {
    if (!user) {
      setBalance(0);
      setTier('free');
      setLoading(false);
      return;
    }

    try {
      const response = await tokensAPI.getBalance();
      setBalance(response.data.balance || 0);
      setTier(response.data.tier || 'free');
      setSubscription(response.data.subscription || null);
    } catch (error) {
      console.error('Erreur chargement solde:', error);
      // Fallback sur les données user si disponibles
      if (user.credits_balance !== undefined) {
        setBalance(user.credits_balance);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Charger au montage et quand l'user change
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Consommer des tokens (appelé après une recherche)
  const consumeTokens = async (amount, action) => {
    if (balance < amount) {
      throw new Error('Solde insuffisant');
    }

    try {
      const response = await tokensAPI.consume({ amount, action });
      setBalance(response.data.new_balance);
      setLastTransaction({
        type: 'debit',
        amount,
        action,
        timestamp: new Date()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Vérifier si une action est possible
  const canAfford = (amount) => {
    return balance >= amount;
  };

  // Estimer le coût d'une recherche selon le tier
  const getSearchCost = (searchTier) => {
    const costs = {
      standard: 0,    // Groq seul = gratuit
      premium: 50,    // Groq + Manus
      elite: 100      // Groq + Manus + Perplexity
    };
    return costs[searchTier] || 0;
  };

  // Rafraîchir après un achat
  const refreshAfterPurchase = () => {
    fetchBalance();
  };

  const value = {
    balance,
    tier,
    subscription,
    loading,
    lastTransaction,
    fetchBalance,
    consumeTokens,
    canAfford,
    getSearchCost,
    refreshAfterPurchase
  };

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
};

export const useTokens = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
};

export default TokenContext;
